import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Filter, X, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const empty = { sector: "", company: "", date_from: "", date_to: "", business_min: "", business_max: "", legacy_min: "", legacy_max: "", workbook_version: "" };

export default function Submissions() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => { api.get("/admin/workbooks").then((r) => setVersions(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "") params[k] = v; });
    try {
      const res = await api.get("/admin/submissions", { params });
      setRows(res.data);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const exportCsv = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "") params[k] = v; });
    try {
      const res = await api.get("/admin/submissions/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Aikyam_Submissions_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch (e) { toast.error("Export failed."); }
  };

  return (
    <div data-testid="submissions-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline mb-2">Leads &amp; Assessments</div>
          <h1 className="font-serif text-4xl text-oxford tracking-tight">Submissions</h1>
        </div>
        <Button data-testid="export-csv-btn" onClick={exportCsv} className="btn-outline h-10 px-5 mt-2">
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[var(--brdr)] rounded-sm p-6 shadow-sm mt-8">
        <div className="flex items-center gap-2 mb-4 text-[var(--text2)]"><Filter className="h-4 w-4" /><span className="overline">Filters</span></div>
        <div className="grid md:grid-cols-4 gap-4">
          <FInput label="Sector" v={filters.sector} onChange={(v) => setF("sector", v)} testid="filter-sector" />
          <FInput label="Company" v={filters.company} onChange={(v) => setF("company", v)} testid="filter-company" />
          <FInput label="Date From" type="date" v={filters.date_from} onChange={(v) => setF("date_from", v)} testid="filter-date-from" />
          <FInput label="Date To" type="date" v={filters.date_to} onChange={(v) => setF("date_to", v)} testid="filter-date-to" />
          <FInput label="Business Min" type="number" v={filters.business_min} onChange={(v) => setF("business_min", v)} testid="filter-bmin" />
          <FInput label="Business Max" type="number" v={filters.business_max} onChange={(v) => setF("business_max", v)} testid="filter-bmax" />
          <FInput label="Legacy Min" type="number" v={filters.legacy_min} onChange={(v) => setF("legacy_min", v)} testid="filter-lmin" />
          <FInput label="Legacy Max" type="number" v={filters.legacy_max} onChange={(v) => setF("legacy_max", v)} testid="filter-lmax" />
          <div>
            <label className="overline">Workbook Version</label>
            <select data-testid="filter-workbook-version" value={filters.workbook_version}
              onChange={(e) => setF("workbook_version", e.target.value)}
              className="mt-1.5 h-10 w-full bg-white border border-[var(--brdr)] rounded-md px-3 text-sm">
              <option value="">All versions</option>
              {versions.map((w) => (
                <option key={w.version} value={w.version}>v{w.version}{w.active ? " (active)" : ""} — {w.question_count}q</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-5">
          <Button data-testid="apply-filters-btn" onClick={load} className="btn-primary text-white h-10 px-6">Apply</Button>
          <button data-testid="clear-filters-btn" onClick={() => { setFilters(empty); setTimeout(load, 0); }}
            className="text-sm text-[var(--text2)] flex items-center gap-1 hover:text-oxford"><X className="h-3.5 w-3.5" /> Clear</button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[var(--brdr)] rounded-sm shadow-sm mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-[var(--brdr)]">
              {["Name", "Company", "Sector", "Designation", "Email", "Phone", "Date", "Business", "Legacy"].map((h) => (
                <th key={h} className="px-5 py-4 overline whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-5 py-10 text-center text-[var(--text2)]">Loading…</td></tr>
            ) : rows.length ? rows.map((r) => (
              <tr key={r.assessment_id} data-testid={`submission-row-${r.assessment_id}`}
                onClick={() => navigate(`/admin/submissions/${r.assessment_id}`)}
                className="border-b border-[var(--brdr)] last:border-0 hover:bg-alabaster cursor-pointer">
                <td className="px-5 py-4 font-medium text-oxford whitespace-nowrap">{r.name}</td>
                <td className="px-5 py-4 whitespace-nowrap">{r.company}</td>
                <td className="px-5 py-4 whitespace-nowrap">{r.sector}</td>
                <td className="px-5 py-4 whitespace-nowrap">{r.designation}</td>
                <td className="px-5 py-4 whitespace-nowrap text-[var(--text2)]">{r.email}</td>
                <td className="px-5 py-4 whitespace-nowrap text-[var(--text2)]">{r.phone}</td>
                <td className="px-5 py-4 whitespace-nowrap text-[var(--text2)]">{r.submitted_at?.slice(0, 10)}</td>
                <td className="px-5 py-4 font-mono text-oxford">{r.business_score?.toFixed(1) ?? "—"}</td>
                <td className="px-5 py-4 font-mono text-gold">{r.legacy_score?.toFixed(1) ?? "—"}</td>
              </tr>
            )) : (
              <tr><td colSpan={9} className="px-5 py-10 text-center text-[var(--text2)]">No submissions match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FInput = ({ label, v, onChange, type = "text", testid }) => (
  <div>
    <label className="overline">{label}</label>
    <Input data-testid={testid} type={type} value={v} onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 h-10 bg-white border-[var(--brdr)]" />
  </div>
);
